export type HomeFirstScreenMediaType = 'image' | 'video' | 'banner'

export type HomeFirstScreenMediaUnit = {
  key: string
  type: 'card' | 'banner'
  mediaType: HomeFirstScreenMediaType
}

export type HomeFirstScreenMediaReadyRecord = {
  readyAtSinceRouteMs: number
  sequence: number
}

export type HomeMediaReadinessRumFields = {
  target_image_count: number
  target_video_count: number
  first_media_ready_since_route_ms?: number
  first_ready_media_type?: HomeFirstScreenMediaType
  all_cards_ready_since_route_ms?: number
  banner_ready_since_route_ms?: number
  last_media_callback_since_route_ms?: number
  last_ready_media_type?: HomeFirstScreenMediaType
}

const roundMs = (value: number) => Math.round(Math.max(0, value) * 100) / 100

const isUsableReadyRecord = (
  record: HomeFirstScreenMediaReadyRecord | undefined
): record is HomeFirstScreenMediaReadyRecord =>
  !!record &&
  Number.isFinite(record.readyAtSinceRouteMs) &&
  record.readyAtSinceRouteMs >= 0 &&
  Number.isFinite(record.sequence)

export const collectHomeMediaReadinessRumFields = (
  targetUnits: readonly HomeFirstScreenMediaUnit[],
  readyRecords: ReadonlyMap<string, HomeFirstScreenMediaReadyRecord>
): HomeMediaReadinessRumFields => {
  let targetImageCount = 0
  let targetVideoCount = 0
  let targetCardCount = 0
  let readyCardCount = 0
  let targetBannerCount = 0
  let readyBannerCount = 0
  let readyTargetCount = 0
  let allCardsReadyAt = 0
  let allBannersReadyAt = 0
  let firstReadyUnit: HomeFirstScreenMediaUnit | undefined
  let firstReadyRecord: HomeFirstScreenMediaReadyRecord | undefined
  let lastReadyUnit: HomeFirstScreenMediaUnit | undefined
  let lastReadyRecord: HomeFirstScreenMediaReadyRecord | undefined

  targetUnits.forEach((unit) => {
    if (unit.mediaType === 'image') targetImageCount += 1
    if (unit.mediaType === 'video') targetVideoCount += 1

    if (unit.type === 'card') targetCardCount += 1
    if (unit.type === 'banner') targetBannerCount += 1

    const readyRecord = readyRecords.get(unit.key)
    if (!isUsableReadyRecord(readyRecord)) return
    readyTargetCount += 1

    if (unit.type === 'card') {
      readyCardCount += 1
      allCardsReadyAt = Math.max(
        allCardsReadyAt,
        readyRecord.readyAtSinceRouteMs
      )
    } else {
      readyBannerCount += 1
      allBannersReadyAt = Math.max(
        allBannersReadyAt,
        readyRecord.readyAtSinceRouteMs
      )
    }

    if (
      !firstReadyRecord ||
      readyRecord.readyAtSinceRouteMs < firstReadyRecord.readyAtSinceRouteMs ||
      (readyRecord.readyAtSinceRouteMs ===
        firstReadyRecord.readyAtSinceRouteMs &&
        readyRecord.sequence < firstReadyRecord.sequence)
    ) {
      firstReadyUnit = unit
      firstReadyRecord = readyRecord
    }

    if (
      !lastReadyRecord ||
      readyRecord.readyAtSinceRouteMs > lastReadyRecord.readyAtSinceRouteMs ||
      (readyRecord.readyAtSinceRouteMs ===
        lastReadyRecord.readyAtSinceRouteMs &&
        readyRecord.sequence > lastReadyRecord.sequence)
    ) {
      lastReadyUnit = unit
      lastReadyRecord = readyRecord
    }
  })

  return {
    target_image_count: targetImageCount,
    target_video_count: targetVideoCount,
    ...(firstReadyUnit && firstReadyRecord
      ? {
          first_media_ready_since_route_ms: roundMs(
            firstReadyRecord.readyAtSinceRouteMs
          ),
          first_ready_media_type: firstReadyUnit.mediaType,
        }
      : {}),
    ...(targetCardCount > 0 && readyCardCount === targetCardCount
      ? { all_cards_ready_since_route_ms: roundMs(allCardsReadyAt) }
      : {}),
    ...(targetBannerCount > 0 && readyBannerCount === targetBannerCount
      ? { banner_ready_since_route_ms: roundMs(allBannersReadyAt) }
      : {}),
    ...(targetUnits.length > 0 &&
    readyTargetCount === targetUnits.length &&
    lastReadyUnit &&
    lastReadyRecord
      ? {
          last_media_callback_since_route_ms: roundMs(
            lastReadyRecord.readyAtSinceRouteMs
          ),
          last_ready_media_type: lastReadyUnit.mediaType,
        }
      : {}),
  }
}
